// src/contexts/MessageStateContext.tsx
"use client";

import { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import type { Message } from '@/lib/firebase';
import type { SelectedItem } from '@/types';
import { EnhancedFirebaseService } from '@/services/firebaseService';

interface MessageStateContextType {
  // State
  messages: Message[];
  selectedItems: SelectedItem[];
  messagesLoaded: boolean; // Track if initial message load is complete
  
  // Actions
  setSelectedItems: (items: SelectedItem[]) => void;
}

const MessageStateContext = createContext<MessageStateContextType | null>(null);

export const useMessageState = () => {
  const context = useContext(MessageStateContext);
  if (!context) {
    throw new Error('useMessageState must be used within MessageStateProvider');
  }
  return context;
};

interface MessageStateProviderProps {
  children: ReactNode;
  assistantId: string;
  initialSelectedItems?: SelectedItem[];
}

export const MessageStateProvider: React.FC<MessageStateProviderProps> = ({
  children,
  assistantId,
  initialSelectedItems = []
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>(initialSelectedItems);
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  // Subscribe to Firestore messages
  useEffect(() => {
    if (!assistantId) {
      setMessages([]);
      setMessagesLoaded(true); // No assistant = no messages to load
      return;
    }

    const unsubscribe = EnhancedFirebaseService.onMessages(
      assistantId,
      (newMessages: Message[]) => {
        setMessages(newMessages);
        setMessagesLoaded(true); // Mark as loaded when we get the first response
      }
    );

    return () => {
      unsubscribe();
    };
  }, [assistantId]);

  const handleSetSelectedItems = useCallback((items: SelectedItem[]) => {
    setSelectedItems(items);
  }, []);

  const contextValue = useMemo((): MessageStateContextType => ({
    messages,
    selectedItems,
    messagesLoaded,
    setSelectedItems: handleSetSelectedItems
  }), [messages, selectedItems, messagesLoaded, handleSetSelectedItems]);

  return (
    <MessageStateContext.Provider value={contextValue}>
      {children}
    </MessageStateContext.Provider>
  );
};